
import React, { useEffect, useState } from "react";
import { ValidatorForm } from "react-form-validator-core";
import ReactSwitch from "react-switch";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { createCustomerSchema ,getCustomerSchema, UpdateCustomerSchema, createTransactionSchema, getTransactionSchema, UpdateTransactionSchema} from "../../components/actions/schema";
import Loader from "../../components/utils/Loader";

function SchemaPage() {
  const clientId = sessionStorage.getItem("code");
  const [activeTab, setActiveTab] = useState("customer");
  const [schemas, setSchemas] = useState({ customer: [], transaction: [] });
  const [filteredData, setFilteredData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [globalFilter, setGlobalFilter] = useState("");
  const [formData, setFormData] = useState({
    clientid: clientId,
    merchantCode: clientId,
    jsonfield: "",
    displayfield: "",
    fieldtype: "",
    datatype: "",
    isdisplay: 0,
    ident: 0,
    id: null,
  });
  const [notification, setNotification] = useState({ show: false, type: "", message: "" });

  const options = [
    { label: "String", value: "string" },
    { label: "Integer", value: "integer" },
    { label: "Float", value: "float" },
  ];

  // Fetch schemas
  const fetchSchemas = async () => {
    setIsFetching(true);
    try {
      const [customerRes, transactionRes] = await Promise.all([
        getCustomerSchema(clientId),
        getTransactionSchema(clientId),
      ]);
      setSchemas({
        customer: customerRes?.data || [],
        transaction: transactionRes?.data || [],
      });
      setFilteredData(activeTab === "customer" ? customerRes?.data || [] : transactionRes?.data || []);
    } catch (error) {
      setNotification({ show: true, type: "error", message: "Failed to fetch schemas" });
      setTimeout(() => setNotification({ show: false, type: "", message: "" }), 2000);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchSchemas();
  }, []);

  useEffect(() => {
    const data = schemas[activeTab];
    if (globalFilter) {
      const lowerCaseFilter = globalFilter.toLowerCase();
      const filtered = data.filter(
        (item) =>
          item.displayfield?.toLowerCase().includes(lowerCaseFilter) ||
          item.fieldtype?.toLowerCase().includes(lowerCaseFilter) ||
          item.datatype?.toLowerCase().includes(lowerCaseFilter) ||
          item.id?.toString().includes(lowerCaseFilter)
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
    setCurrentPage(1);
  }, [globalFilter, schemas, activeTab]);

  const handleViewModal = () => {
    setIsEditMode(false);
    setFormData({
      clientid: clientId,
      merchantCode: clientId,
      jsonfield: "",
      displayfield: "",
      fieldtype: "",
      datatype: "",
      isdisplay: 0,
      ident: 0,
      id: null,
    });
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setIsEditMode(true);
    setFormData({
      clientid: clientId,
      merchantCode: clientId,
      jsonfield: row.jsonfield,
      displayfield: row.displayfield || row.Displayfield,
      fieldtype: row.fieldtype || row.DataType,
      datatype: row.DataType || row.fieldtype,
      isdisplay: row.isdisplay === "1" || row.isdisplay === 1 ? 1 : 0, // Ensure correct toggle state
      ident: row.ident === "1" || row.Ident === true || row.ident === 1 ? 1 : 0,
      id: row.id || row.Id,
    });
    setShowModal(true);
  };

  const toCamelCase = (str) => {
    return str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .split(" ")
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word?.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    if (name === "displayfield") {
      updatedFormData.jsonfield = toCamelCase(value);
    }
    setFormData(updatedFormData);
  };

  const onSelectChange = (e) => {
    setFormData({ ...formData, fieldtype: e.target.value, datatype: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (activeTab === "customer") {
        if (isEditMode) {
          response = await UpdateCustomerSchema(formData.id, formData.isdisplay, formData.ident);
        } else {
          response = await createCustomerSchema(formData);
        }
      } else {
        if (isEditMode) {
          response = await UpdateTransactionSchema(formData.id, formData.ident);
        } else {
          response = await createTransactionSchema(formData);
        }
      }
      setLoading(false);
      if (response.message === "success") {
        setNotification({
          show: true,
          type: "success",
          message: isEditMode ? `${activeTab} Schema Updated successfully` : `${activeTab} Schema Created successfully`,
        });
        setFormData({
          clientid: clientId,
          merchantCode: clientId,
          jsonfield: "",
          displayfield: "",
          fieldtype: "",
          datatype: "",
          isdisplay: 0,
          ident: 0,
          id: null,
        });
        setShowModal(false);
        fetchSchemas();
        setTimeout(() => setNotification({ show: false, type: "", message: "" }), 2000);
      } else {
        throw new Error("Failed");
      }
    } catch (error) {
      setLoading(false);
      setNotification({
        show: true,
        type: "error",
        message: isEditMode ? `${activeTab} Schema Update Failed` : `${activeTab} Schema Creation Failed`,
      });
      setTimeout(() => setNotification({ show: false, type: "", message: "" }), 2000);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "string":
        return "bg-blue-100 text-blue-800";
      case "integer":
        return "bg-green-100 text-green-800";
      case "float":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "string":
        return "ri-text";
      case "integer":
        return "ri-hashtag";
      case "float":
        return "ri-number-9";
      default:
        return "ri-database-line";
    }
  };

  const getCurrentSchema = () => ({
    name: activeTab === "customer" ? "Customer Schema" : "Transaction Schema",
    tableName: activeTab === "customer" ? "customers" : "transactions",
    fields: filteredData,
    recordCount: filteredData.length,
    updatedAt: new Date().toISOString(),
  });

  const columns = [
    { name: "ID", selector: (row) => row.id || row.Id, sortable: true },
    { name: "Display Field Name", selector: (row) => row.displayfield || row.Displayfield, sortable: true },
    { name: "JSON Field", selector: (row) => row.jsonfield, sortable: true },
    { name: "Field Type", selector: (row) => row.fieldtype || row.DataType, sortable: true },
    activeTab === "customer" && {
      name: "Is Display",
      selector: (row) => (row.isdisplay === "1" || row.isdisplay === 1 ? "True" : "False"),
      sortable: true,
    },
    {
      name: "Ident",
      selector: (row) => (row.ident === "1" || row.Ident === true || row.ident === 1 ? "True" : "False"),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <button className="btn btn-sm btn-primary" onClick={() => handleEdit(row)}>
          <i className="ri-edit-line"></i>
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ].filter(Boolean);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {isFetching && <Loader />}
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Schema Management</h1>
            <p className="text-gray-600 mt-1">Manage database schemas and field definitions</p>
          </div>
          <button
            onClick={handleViewModal}
            className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
            Add New Field
          </button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-database-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Schemas</p>
                <p className="text-2xl font-bold text-gray-900">2</p>
                <p className="text-green-600 text-xs font-semibold">Active schemas</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-file-list-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Fields</p>
                <p className="text-2xl font-bold text-gray-900">{schemas.customer.length + schemas.transaction.length}</p>
                <p className="text-green-600 text-xs font-semibold">Defined fields</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-bar-chart-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">{filteredData.length}</p>
                <p className="text-green-600 text-xs font-semibold">Database records</p>
              </div>
            </div>
          </div>
        </div>

        {/* Schema Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {[
                { id: "customer", label: "Customer Schema", icon: "ri-user-line", count: schemas.customer.length },
                { id: "transaction", label: "Transaction Schema", icon: "ri-exchange-line", count: schemas.transaction.length },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 px-6 py-4 font-semibold text-sm border-b-2 transition-colors cursor-pointer ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600 bg-blue-50"
                      : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <i className={`${tab.icon} text-lg`}></i>
                  {tab.label}
                  <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-semibold">
                    {tab.count} fields
                  </span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Schema Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <i className={`${activeTab === "customer" ? "ri-user-line" : "ri-exchange-line"} text-white text-2xl`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{getCurrentSchema().name}</h3>
                  <p className="text-gray-600">
                    Table: <span className="font-mono font-semibold">{getCurrentSchema().tableName}</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {getCurrentSchema().recordCount.toLocaleString()} records • Last updated{" "}
                    {new Date(getCurrentSchema().updatedAt).toLocaleDateString("en-IN")}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleViewModal}
                  className="bg-blue-50 text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Add Field
                </button>
              </div>
            </div>

            {/* Fields Table */}
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Field Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        JSON Field
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data Type
                      </th>
                      {activeTab === "customer" && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Is Display
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ident
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((field, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <i className={`${getTypeIcon(field.fieldtype || field.DataType)} text-blue-600 text-sm`}></i>
                            </div>
                            <span className="font-semibold text-gray-900 font-mono">{field.displayfield || field.Displayfield}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{field.jsonfield}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(field.fieldtype || field.DataType)}`}>
                            {field.fieldtype || field.DataType}
                          </span>
                        </td>
                        {activeTab === "customer" && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                field.isdisplay === "1" || field.isdisplay === 1 ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {field.isdisplay === "1" || field.isdisplay === 1 ? "True" : "False"}
                            </span>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              field.ident === "1" || field.Ident === true || field.ident === 1 ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {field.ident === "1" || field.Ident === true || field.ident === 1 ? "True" : "False"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            className="text-blue-600 hover:text-blue-800 cursor-pointer"
                            title="Edit Field"
                            onClick={() => handleEdit(field)}
                          >
                            <i className="ri-edit-line text-lg"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, filteredData.length)} of{" "}
                    {filteredData.length} entries
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Previous page"
                    >
                      Previous
                    </button>
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      const pageNum = index + 1 + Math.max(0, currentPage - 3);
                      if (pageNum > totalPages) return null;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPage === pageNum
                              ? "bg-blue-500 text-white border-blue-500"
                              : "border-gray-300 hover:bg-gray-50"
                          }`}
                          aria-label={`Go to page ${pageNum}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Next page"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal for Add/Edit */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isEditMode ? `Edit ${activeTab} Schema` : `Add ${activeTab} Schema`}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              {loading && <Loader />}
              <ValidatorForm onSubmit={handleSubmit}>
                <div className="form-group">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-600 text-sm font-medium">Display Field Name</label>
                      <input
                        placeholder="Enter Display Field Name"
                        onChange={handleChange}
                        value={formData.displayfield}
                        type="text"
                        name="displayfield"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={isEditMode}
                      />
                    </div>
                    <div>
                      <label className="text-gray-600 text-sm font-medium">Field Type</label>
                      <select
                        name="fieldtype"
                        value={formData.fieldtype || formData.datatype}
                        onChange={onSelectChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={isEditMode}
                      >
                        <option value="">Select</option>
                        {options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {activeTab === "customer" && (
                      <div className="flex items-center gap-3">
                        <p className="text-gray-600 text-sm font-medium">Is Display</p>
                        <ReactSwitch
                          checked={formData.isdisplay === 1}
                          onChange={(checked) => setFormData((prev) => ({ ...prev, isdisplay: checked ? 1 : 0 }))}
                          onColor="#0d6efd"
                          offColor="#6c757d"
                          checkedIcon={false}
                          uncheckedIcon={false}
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <p className="text-gray-600 text-sm font-medium">Ident</p>
                      <ReactSwitch
                        checked={formData.ident === 1}
                        onChange={(checked) => setFormData((prev) => ({ ...prev, ident: checked ? 1 : 0 }))}
                        onColor="#0d6efd"
                        offColor="#6c757d"
                        checkedIcon={false}
                        uncheckedIcon={false}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer"
                  >
                    {isEditMode ? "Update" : "Submit"}
                  </button>
                </div>
              </ValidatorForm>
            </div>
          </div>
        )}

        {/* Notification Popup */}
        {notification.show && (
          <div className="fixed top-4 right-4 z-50">
            <div
              className={`rounded-xl p-4 shadow-lg border ${
                notification.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              <div className="flex items-center gap-3">
                <i
                  className={`ri-${
                    notification.type === "success" ? "checkbox-circle-line" : "error-warning-line"
                  } text-xl`}
                ></i>
                <p className="font-semibold">{notification.message}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default SchemaPage;
